import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import { useApiContext } from 'context/ApiContext';
import { useGlobalContext } from 'context/GlobalContext';
import { KEYS } from 'helpers/react-query.config';
import { AxiosServiceError } from 'interfaces/general';

interface Props {
  ticketId?: string;
  isOpen?: boolean;
}

/**
 * @file useCloseTicketServices.ts
 *
 * Custom hook for handling services related to closing a ticket.
 * This hook provides queries and mutations to fetch ticket components,
 * closed details, and to update the ticket status to closed.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.ticketId - The ID of the ticket.
 * @param {boolean} props.isOpen - Flag indicating if the ticket is open.
 */
const useCloseTicketServices = ({ ticketId, isOpen }: Props = {}) => {
  const { showApiError, showSuccess } = useGlobalContext();
  const { product: productApi, ticket: ticketApi } = useApiContext();
  const queryClient = useQueryClient();
  const { t } = useTranslation('global');

  const getTicketComponentsByTicketId = useQuery(
    [KEYS.product.tickets.getTicketComponentsByTicketId(), ticketId],
    () => productApi.tickets.getTicketComponentsByTicketId(Number(ticketId)),
    {
      select: (data) => data?.data?.data,
      refetchOnWindowFocus: false,
      enabled: !!ticketId && isOpen,
      onError: (error: AxiosServiceError) =>
        showApiError(error, t('errorMessages.ticketComponents'))
    }
  );

  const getTicketClosedDetails = useQuery(
    [KEYS.ticket.closedDetails.list(), ticketId],
    () =>
      ticketApi.closedDetails.GetTicketClosedDetails({
        ticketId: Number(ticketId)
      }),
    {
      select: (data) => data?.data?.data,
      refetchOnWindowFocus: false,
      enabled: !!ticketId && isOpen
    }
  );

  const updateToClosed = useMutation(
    (ticketIdToUpdate: string) =>
      ticketApi.tickets.meUpdateTicketsToOnClosedStatus(ticketIdToUpdate),
    {
      onSuccess: async (data) => {
        await Promise.all([
          queryClient.invalidateQueries(
            KEYS.ticket.tickets.detail(Number(data?.data?.data?.ticketId))
          ),
          queryClient.invalidateQueries(KEYS.ticket.logbook.list()),
          queryClient.invalidateQueries(
            KEYS.ticket.tickets.getAvailableStatuses()
          )
        ]);
        showSuccess(
          t('success.ticketFinished', {
            ticketId: Number(data?.data?.data?.ticketId)
          }),
          3000
        );
      },
      onError: (error: AxiosServiceError) => {
        showApiError(error, t('common.commonMessages.genericRequestError'));
      }
    }
  );

  return {
    getTicketComponentsByTicketId,
    getTicketClosedDetails,
    updateToClosed
  };
};

export default useCloseTicketServices;
